import {
  RepositoryPicker,
  type RepositoryPickerTopRepositoriesQuery,
  type RepositoryPickerRepository$data as Repository,
} from '@github-ui/item-picker/repository'
import {Box, Text} from '@primer/react'
import {Suspense, useId} from 'react'
import {type PreloadedQuery, graphql} from 'react-relay'
import {TemplateListPane, type TemplateListVariant} from './TemplateListPane'
import type {IssueCreatePayload} from '.'
import {TemplateListLoading} from './TemplateList'

export const RepositoryTemplates = graphql`
  query RepositoryAndTemplatePickerDialogQuery($id: ID!) {
    node(id: $id) {
      ... on Repository {
        # eslint-disable-next-line relay/must-colocate-fragment-spreads
        ...RepositoryPickerRepositoryIssueTemplates
      }
    }
  }
`

export type RepositoryAndTemplatePickerDialogProps = {
  repository: Repository | undefined
  setRepository: (repository: Repository | undefined) => void
  topReposQueryRef: PreloadedQuery<RepositoryPickerTopRepositoriesQuery>
  organization?: string
  onTemplateSelected: (template: IssueCreatePayload) => void
  variant?: TemplateListVariant
}

export function RepositoryAndTemplatePickerDialog({
  repository,
  setRepository,
  topReposQueryRef,
  organization,
  onTemplateSelected,
  variant,
}: RepositoryAndTemplatePickerDialogProps) {
  const descriptionId = useId()
  return (
    <Box>
      <Field name="Repository" />
      <Box sx={{width: 'content', mb: 2}}>
        <RepositoryPicker
          aria-describedby={descriptionId}
          initialRepository={repository}
          onSelect={setRepository}
          organization={organization}
          topReposQueryRef={topReposQueryRef}
          focusRepositoryPicker={true}
          enforceAtleastOneSelected={true}
          options={{hasIssuesEnabled: true}}
        />
      </Box>
      <Field name="Templates and forms" />
      <Suspense fallback={<TemplateListLoading />}>
        {repository && (
          <TemplateListPane
            variant={variant}
            repository={repository}
            onTemplateSelected={onTemplateSelected}
            descriptionId={descriptionId}
          />
        )}
      </Suspense>
    </Box>
  )
}

function Field({name}: {name: string}) {
  return (
    <Box sx={{pt: 1, pb: 1}}>
      <Text sx={{fontWeight: 'bold'}}>{name}</Text>
      <Text sx={{color: 'danger.fg', ml: 1, mb: 1}}>*</Text>
    </Box>
  )
}

try{ RepositoryAndTemplatePickerDialog.displayName ||= 'RepositoryAndTemplatePickerDialog' } catch {}
try{ Field.displayName ||= 'Field' } catch {}